<template>
  <div ref="searchBarRef" class="relative flex">
    <div class="peer/searchbar order-last w-full">
      <div class="flex gap-16" :class="{ 'mr-20': isBuybackEnabled }">
        <Textbox
          ref="textbox"
          :input-id="'search'"
          :input-name="SCHEMA_SEARCH_NAME"
          :is-listbox-open="isSearchBarActive"
          :listbox-results-message
          :value="searchInput"
          @change="changeHandler"
          @click.stop
          @reset="clearSearchBar"
          @submit="goToSearch"
        />
        <button
          v-show="isSearchBarActive"
          class="text-nowrap md:hidden"
          type="button"
          @click="closeSearchBar"
        >
          {{ i18n(translations.cancelLabel) }}
        </button>
      </div>
    </div>

    <Transition
      enter-active-class="ease-out duration-300"
      enter-from-class="[clip-path:inset(0_0_100%)]"
      enter-to-class="[clip-path:inset(0_0_0%)]"
      leave-active-class="ease-in duration-100"
      leave-from-class="[clip-path:inset(0_0_0%)]"
      leave-to-class="[clip-path:inset(0_0_100%)]"
    >
      <div
        v-if="isSearchResultsVisible || isPopularSearchesVisible"
        class="bg-surface-default-low border-static-default-low absolute inset-x-0 top-full mt-8 h-screen max-h-[calc(100vh-var(--dropdown-top))] overflow-y-auto overscroll-y-contain border-t transition-[clip-path] md:fixed md:top-[var(--dropdown-top)] md:h-auto"
        :class="{ hidden: headerIsHidden }"
        data-test="search-bar-dropdown"
        :style="`--dropdown-top: ${searchBarBottom + 8}px`"
      >
        <div v-if="isSearchResultsVisible">
          <div
            v-if="!isLoading && !isTyping"
            class="flex flex-col-reverse gap-16 pt-24 md:flex-row"
          >
            <SuggestionsSection
              class="px-24 md:min-w-256 md:pb-40 md:pl-40 md:pr-0"
              :input-value="searchInput"
              name="suggestions"
              :suggestions="suggestionsHits"
              :title="i18n(translations.suggestedSearches)"
              @select="handleSelect"
              @submit="closeSearchBar"
            />
            <ProductSection
              class="grow px-24 md:px-0"
              :products="productsHits"
              @submit="closeSearchBar"
            />
          </div>
          <Skeleton v-else />
        </div>
        <div
          v-else-if="isPopularSearchesVisible"
          class="mx-auto max-w-[650px] px-28 pb-40 pt-24"
        >
          <SuggestionsSection
            name="popularSearches"
            :suggestions="popularSearches"
            :title="i18n(translations.popularSearches)"
            @select="handleSelect"
            @submit="closeSearchBar"
          />
        </div>
      </div>
    </Transition>

    <div
      v-if="isBuybackEnabled"
      class="mr-12 grid shrink-0 transition-[grid-template-columns,margin]"
      :class="
        isSearchBarActive
          ? 'grid-cols-[0fr] mr-20 duration-300'
          : 'grid-cols-[1fr] duration-150 ease-[cubic-bezier(0.5,0,0.2,1)]'
      "
    >
      <div class="flex justify-end overflow-hidden">
        <TradeInButton
          class="relative"
          :class="hasImproveSearchUIExperiment ? 'ml-12 md:ml-20' : ' ml-20'"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from '#imports'
import { computed, ref, useTemplateRef, watch } from 'vue'

import { getPopular } from '@backmarket/http-api/src/api-specs-search-reco/search/searchPopular'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import {
  type Fn,
  isIOS,
  useEventListener,
  useFocusWithin,
  useScrollLock,
  watchDebounced,
} from '@vueuse/core'

import TradeInButton from '../../../navigation/components/TradeInButton.vue'
import type { Link } from '../../algolia/algoliaFunctions'
import { useSearchBar } from '../../composables/useSearchBar'

import ProductSection from './ProductSection.vue'
import translations from './SearchBar.translations'
import Skeleton from './Skeleton.vue'
import SuggestionsSection from './SuggestionsSection.vue'
import Textbox from './Textbox.vue'

const i18n = useI18n()
const locale = useI18nLocale()
const router = useRouter()
const tracking = useTracking()
const textbox = ref()
const searchBarElement = useTemplateRef('searchBarRef')
const searchBarBottom = ref(0)

const {
  productsHits,
  suggestionsHits,
  query: searchInput,
  isSearchBarActive,
  isLoading,
  isTyping,
  clearSearchBar,
  openSearchBar,
  closeSearchBar,
  fetchSearchBarConfiguration,
} = useSearchBar({
  debounce: true,
  debounceTime: 300,
})

await fetchSearchBarConfiguration()

const { isBuybackEnabled = false } = defineProps<{
  headerIsHidden: boolean
  isBuybackEnabled?: boolean
}>()

const searchBarRef = ref<HTMLElement | null>(null)
const { data: popularSearchesPayload } = useHttpLazyFetch(getPopular)
const experiments = useExperiments()

const SCHEMA_SEARCH_NAME = 'q'
const hasImproveSearchUIExperiment =
  experiments['experiment.improveSearchUi'] === 'new'

const popularSearches = computed<Link[]>(() => {
  if (!popularSearchesPayload.value) {
    return []
  }

  return popularSearchesPayload.value
    .filter(
      (item) =>
        !(
          item.link.type === 'internal' && item.link.params.slugV2 === undefined
        ),
    )
    .map((item) => {
      return {
        highlightedTitle: item.highlightedTitle,
        title: item.title,
        id: item.landingId,
        url:
          item.link.type === 'external'
            ? item.link.href
            : `/${locale}/l/${item.link.params.slugV2}/${item.landingId}`,
      }
    })
})

const listboxResultsMessage = computed(() => {
  const resultsCount = productsHits.value.length
  const searchQuery = searchInput.value
  const messageWhenQuery = i18n(translations.resultsListing, {
    resultsCount,
    searchQuery,
  })

  return searchInput.value
    ? messageWhenQuery
    : i18n(translations.textboxPlaceholder)
})

function resetInput() {
  searchInput.value = ''
}

function changeHandler(value: string) {
  searchInput.value = value
}

function goToSearch() {
  router.push(`/${locale}/search?q=${searchInput.value}`)
  resetInput()
  closeSearchBar()
  tracking.trackFormSubmit({
    zone: 'header_searchbar',
    name: searchInput.value,
    payload: {
      index_used: 0,
      search_type: 'suggested_models',
    },
  })
}

const isSearchResultsVisible = computed(() => {
  return (
    isSearchBarActive.value &&
    searchInput.value &&
    (productsHits.value.length > 0 ||
      suggestionsHits.value.length > 0 ||
      isLoading.value ||
      isTyping.value)
  )
})

const isPopularSearchesVisible = computed(() => {
  return (
    isSearchBarActive.value &&
    !isLoading.value &&
    !isTyping.value &&
    (popularSearches.value?.length ?? 0) > 0
  )
})

if (import.meta.client) {
  const { focused: isFocused } = useFocusWithin(searchBarRef)

  watchDebounced(
    isFocused,
    () => {
      if (isFocused.value) {
        openSearchBar()
      } else {
        closeSearchBar()
      }
    },
    { debounce: 1 },
  )

  const isPageScrollLocked = useScrollLock(document?.body)
  let stopTouchMoveListener: Fn

  watch([isPopularSearchesVisible, isSearchResultsVisible], () => {
    isPageScrollLocked.value = !!(
      isPopularSearchesVisible.value || isSearchResultsVisible.value
    )

    if (isPopularSearchesVisible.value || isSearchResultsVisible.value) {
      searchBarBottom.value =
        searchBarElement.value?.getBoundingClientRect().bottom ?? 0
    }

    if (isIOS) {
      if (isPopularSearchesVisible.value || isSearchResultsVisible.value) {
        stopTouchMoveListener = useEventListener(
          searchBarElement,
          'touchmove',
          () => {
            textbox.value.input.blur()
          },
        )
      } else {
        stopTouchMoveListener?.()
      }
    }
  })
}

function handleSelect(title: string) {
  changeHandler(title)
  textbox.value.handleFocus()
}
</script>
